import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const Consultorios = () => {
  const [consultorios, setConsultorios] = useState([]);
  const [originalConsultorios, setOriginalConsultorios] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedConsultorio, setSelectedConsultorio] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sucursales, setSucursales] = useState([]); // Para el select

  const [formData, setFormData] = useState({
    nombre: '',
    id_sucursal: '',
    activo: true,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userIdPet, setUserIdPet] = useState(null);
  const [userIdRol, setUserIdRol] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const navigate = useNavigate();

  // Decodificar token JWT
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserIdPet(payload.id_pet || null);
        setUserIdRol(payload.id_rol || null);
      } catch (err) {
        console.error('Error al decodificar token:', err);
        setError('Error al decodificar el token');
        setSnackbarMessage('Error al decodificar el token');
        setSnackbarOpen(true);
      }
    }
  }, []);

  // Cargar Consultorios y Sucursales
  useEffect(() => {
    const fetchData = async () => {
      if (!userIdPet || !userIdRol) return;

      try {
        setLoading(true);

        const [consultoriosRes, sucursalesRes] = await Promise.all([
          api.get('/consultorios/'),
          api.get('/sucursales/')
        ]);

        // Filtrar según permisos del usuario
        let filteredConsultorios = consultoriosRes.data;

        if (String(userIdRol) !== "1" && String(userIdRol) !== "SYSTEM_USER") {
          const userSucursalesIds = sucursalesRes.data
            .filter(s => s.id_pet === userIdPet)
            .map(s => s.id_sucursal);

          filteredConsultorios = filteredConsultorios.filter(c =>
            userSucursalesIds.includes(c.id_sucursal)
          );
        }

        setConsultorios(filteredConsultorios);
        setOriginalConsultorios(filteredConsultorios);
        setSucursales(sucursalesRes.data);

        setLoading(false);
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError('Error al cargar consultorios');
        setSnackbarMessage('Error al cargar consultorios');
        setSnackbarOpen(true);
        setLoading(false);
      }
    };

    fetchData();
  }, [userIdPet, userIdRol]);

  // Búsqueda en frontend
  useEffect(() => {
    if (!searchTerm) {
      setConsultorios(originalConsultorios);
      return;
    }

    const filtered = originalConsultorios.filter(c =>
      c.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setConsultorios(filtered);
    setPage(0);
  }, [searchTerm, originalConsultorios]);

  const handleOpenModal = (consultorio = null) => {
    setSelectedConsultorio(consultorio);
    setFormData(
      consultorio
        ? {
            nombre: consultorio.nombre || '',
            id_sucursal: consultorio.id_sucursal || '',
            activo: consultorio.activo !== false,
          }
        : {
            nombre: '',
            id_sucursal: '',
            activo: true,
          }
    );
    setOpenModal(true);
  };

  const handleSave = async () => {
    if (!formData.nombre || !formData.id_sucursal) {
      setSnackbarMessage('Nombre y Sucursal son obligatorios');
      setSnackbarOpen(true);
      return;
    }

    try {
      const payload = {
        nombre: formData.nombre,
        id_sucursal: parseInt(formData.id_sucursal),
        activo: formData.activo,
      };

      if (selectedConsultorio) {
        const res = await api.put(`/consultorios/${selectedConsultorio.id_consultorio}`, payload);
        const updated = res.data;
        setConsultorios(prev =>
          prev.map(c => (c.id_consultorio === updated.id_consultorio ? updated : c))
        );
        setSnackbarMessage('Consultorio actualizado exitosamente');
      } else {
        const res = await api.post('/consultorios/', payload);
        setConsultorios(prev => [...prev, res.data]);
        setSnackbarMessage('Consultorio creado exitosamente');
      }

      setOpenModal(false);
      setSnackbarOpen(true);
    } catch (error) {
      console.error(error);
      setSnackbarMessage('Error al guardar: ' + (error.response?.data?.detail || error.message));
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este consultorio?')) return;

    try {
      await api.delete(`/consultorios/${id}`);
      setConsultorios(prev => prev.filter(c => c.id_consultorio !== id));
      setSnackbarMessage('Consultorio eliminado exitosamente');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Error al eliminar consultorio');
      setSnackbarOpen(true);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const paginatedConsultorios = consultorios.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Consultorios
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar consultorios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1, width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth={false} disableGutters>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenModal()}
              >
                Nuevo Consultorio
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nombre del Consultorio</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Sucursal</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Activo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Creado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actualizado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedConsultorios.map((row) => (
                    <TableRow key={row.id_consultorio}>
                      <TableCell sx={{ textAlign: 'center' }}>{row.id_consultorio}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{row.nombre}</TableCell>
                      <TableCell>
                        {sucursales.find(s => s.id_sucursal === row.id_sucursal)?.nombre_sucursal || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Switch checked={row.activo} disabled size="small" />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{formatDate(row.created_at)}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{formatDate(row.updated_at)}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton color="primary" onClick={() => handleOpenModal(row)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(row.id_consultorio)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginación simple */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2 }}>
              <Button
                variant="contained"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <Typography sx={{ alignSelf: 'center' }}>
                Página {page + 1} de {Math.ceil(consultorios.length / rowsPerPage)}
              </Typography>
              <Button
                variant="contained"
                disabled={page >= Math.ceil(consultorios.length / rowsPerPage) - 1}
                onClick={() => setPage(page + 1)}
              >
                Siguiente
              </Button>
            </Box>
          </>
        )}
      </Container>

      {/* Modal Crear/Editar */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedConsultorio ? 'Editar Consultorio' : 'Nuevo Consultorio'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre del Consultorio"
            fullWidth
            value={formData.nombre}
            onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
            sx={{ mt: 2 }}
            required
          />

          <TextField
            select
            label="Sucursal"
            fullWidth
            value={formData.id_sucursal}
            onChange={(e) => setFormData({ ...formData, id_sucursal: e.target.value })}
            sx={{ mt: 2 }}
            required
          >
            {sucursales.map((suc) => (
              <MenuItem key={suc.id_sucursal} value={suc.id_sucursal}>
                {suc.nombre_sucursal}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={formData.activo}
              onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              color="primary"
            />
            <Typography>Activo</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.nombre || !formData.id_sucursal}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Consultorios;